/**
 * PupRing Custom JavaScript - Bundle Builder Router Module
 * =========================================================
 * Buy 3 for $99 Bundle Builder - Step Router & Choice Manager
 *
 * Features:
 * - BundleStepRouter: Central orchestrator for step transitions
 * - BundleChoiceManager: Handles Together vs Individual choice
 * - Step visibility management
 * - Step indicator updates
 *
 * Dependencies:
 * - custom-bundle-state.js (BundleStateManager)
 * - custom-bundle-ui.js (BundleUIManager)
 * - custom-bundle-together.js (BundleTogetherManager) - lazy loaded
 * - custom-bundle-individual.js (BundleIndividualManager) - lazy loaded
 */

// ═══════════════════════════════════════════════════════════════════════════════
// BUNDLE STEP ROUTER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BundleStepRouter - Central orchestrator for step transitions
 *
 * Responsibilities:
 * - Listens to bundle:step-changed events
 * - Shows/hides step containers
 * - Initializes step-specific managers
 * - Updates step indicator
 */
class BundleStepRouter {
  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager, uiManager) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;

    // Step managers (lazy initialized)
    this.choiceManager = null;
    this.togetherManager = null;
    this.individualManager = null;
    this.upsellsManager = null;
    this.summaryManager = null;

    // Cached product data (fetched once in choice step)
    this.productsDataCache = null;

    // EPIC-001 FIX: Track hide animation timers to prevent race conditions
    // When navigating between steps that use the same container (individual-1 → individual-2),
    // the hide animation timer would fire AFTER the container is shown, hiding it unexpectedly.
    this.hideTimers = new Map();

    // BUG-073 FIX: Flag to track same-container transitions (individual-1 → individual-2)
    // When true, skip exit/enter animations for instant content swap
    this.isSameContainerTransition_flag = false;

    // Bound handlers for cleanup
    this.boundStepChangeHandler = null;

    // Phase 9: Browser history management
    this.boundPopstateHandler = null;
    this.isNavigatingFromHistory = false; // Flag to prevent double history push
  }

  /**
   * BUG-073 FIX: Check if two steps use the same container
   * @param {string} fromStep - Previous step
   * @param {string} toStep - New step
   * @returns {boolean} True if same container
   */
  isSameContainerTransition(fromStep, toStep) {
    const individualSteps = ['individual-1', 'individual-2', 'individual-3'];
    return individualSteps.includes(fromStep) && individualSteps.includes(toStep);
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize the step router
   * BUG-055 FIX: Made async to properly await initializeRestoredStep()
   */
  async init() {
    // BUG-081 FIX: Only init on bundle collection pages
    // Without this guard, updateStepVisibility() hides .product-list on ALL collection pages
    // because it queries .product-list globally and sets display:none when currentStep != 'selection'
    if (!document.querySelector('[data-bundle-builder]')) {
      return;
    }

    // Bind event handler
    // BUG-073 FIX: Capture prevStep to detect same-container transitions
    this.boundStepChangeHandler = (e) => {
      const { step, prevStep, state } = e.detail;
      // Set flag BEFORE calling onStepChange so updateStepVisibility can use it
      this.isSameContainerTransition_flag = this.isSameContainerTransition(prevStep, step);
      this.onStepChange(step, state);
    };
    window.addEventListener('bundle:step-changed', this.boundStepChangeHandler);

    // Set initial step visibility based on current state
    const state = this.stateManager.get();
    this.updateStepVisibility(state.currentStep);
    this.updateStepIndicator(state.currentStep);

    // BUG-044 FIX: Initialize step manager if state was restored to a customization step
    // On page refresh, no bundle:step-changed event fires, so we must manually initialize
    // BUG-055 FIX: Added await to ensure fetch completes before init() returns
    // BUG-082 FIX: Added 'upsells' and 'summary' - page refresh was showing empty container
    if (state.currentStep === 'together' ||
        state.currentStep.startsWith('individual-') ||
        state.currentStep === 'upsells' ||
        state.currentStep === 'summary') {
      await this.initializeRestoredStep(state.currentStep, state);
    }

    // BUG-066 FIX: Handle restored 'choice' step - modal wasn't shown, reset to selection
    // On page refresh with currentStep='choice', the choice modal won't be initialized
    // (only 'together' and 'individual-*' steps are handled above)
    // Reset to 'selection' so user can click Next Step to get the choice modal
    if (state.currentStep === 'choice') {
      this.stateManager.set({ currentStep: 'selection' });
      this.updateStepVisibility('selection');
      this.updateStepIndicator('selection');
    }

    // Phase 9: Setup browser history management
    this.setupHistoryHandling();
  }

  /**
   * BUG-044 FIX: Initialize step manager when state is restored from localStorage
   * This handles page refresh where no bundle:step-changed event fires
   * CRITICAL: Fetches products data first if not cached (page refresh scenario)
   * @param {string} step - The restored step name
   * @param {object} state - The restored state
   */
  async initializeRestoredStep(step, state) {
    // Verify we have the required data to initialize
    if (!state.products || state.products.length < 3) {
      // Not enough products - reset to selection
      this.stateManager.goToStep('selection');
      return;
    }

    if (!state.customizationMode) {
      // No mode selected - go to choice
      this.stateManager.goToStep('choice');
      return;
    }

    // CRITICAL: Fetch products data if not cached (page refresh scenario)
    // On page refresh, productsDataCache is null because it's only populated
    // when going through the choice step normally
    if (!this.productsDataCache) {
      try {
        this.uiManager?.showLoading?.();

        // Fetch product JSON for all 3 products
        const handles = state.products.map(p => p.productHandle);

        const responses = await Promise.allSettled(
          handles.map(handle =>
            fetch(`/products/${handle}.js`).then(r => {
              if (!r.ok) throw new Error(`HTTP ${r.status} for ${handle}`);
              return r.json();
            })
          )
        );

        // BUG-071 FIX: Log any fetch failures for debugging
        responses.forEach((r, i) => {
          if (r.status === 'rejected') {
            console.error(`[BundleRouter] Failed to fetch product ${i} (${handles[i]}):`, r.reason);
          }
        });

        const productsData = responses
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);

        if (productsData.length < 3) {
          // Couldn't load all products - reset to selection
          console.error('[BundleRouter] Could not fetch all 3 products, got:', productsData.length);
          this.uiManager?.showToast?.('Could not load products. Please try again.', 'error');
          this.stateManager.goToStep('selection');
          return;
        }

        // Cache the data for step managers
        this.productsDataCache = productsData;

        // BUG-069 FIX: Backfill requirements on page refresh
        // localStorage has products but requirements may be stale/missing
        this.backfillProductRequirementsOnRestore(productsData);

        // Also analyze and store common variants for choice modal
        const commonVariants = BundleStateManager.findCommonVariants(productsData);
        this.stateManager.set({
          hasCommonVariants: commonVariants.length > 0,
          commonVariants: commonVariants.length > 0 ? commonVariants : null
        });

      } catch (error) {
        console.error('[BundleRouter] Error fetching products for restored step:', error);
        this.uiManager?.showToast?.('Error loading products. Please try again.', 'error');
        this.stateManager.goToStep('selection');
        return;
      } finally {
        this.uiManager?.hideLoading?.();
      }
    }

    // Initialize the appropriate step manager
    // BUG-082 FIX: Added 'upsells' and 'summary' cases for page refresh handling
    switch (step) {
      case 'together':
        await this.initTogetherStep();  // BUG-115 FIX: Added await for async re-fetch
        break;
      case 'individual-1':
      case 'individual-2':
      case 'individual-3':
        this.initIndividualStep(step);
        break;
      case 'upsells':
        this.initUpsellsStep();
        break;
      case 'summary':
        this.initSummaryStep();
        break;
    }
  }

  /**
   * BUG-069 FIX: Backfill requirements after page refresh
   * On page refresh, localStorage has products but requirements may be stale/missing
   * @param {Array} productsData - Array of product JSON objects from Shopify API
   */
  backfillProductRequirementsOnRestore(productsData) {
    const state = this.stateManager.get();
    let hasChanges = false;

    const updatedProducts = state.products.map((product, index) => {
      const productData = productsData[index];
      if (!productData) return product;

      // Get the variant for pendant count extraction
      const variant = productData.variants?.find(v => v.id === product.selectedVariantId);

      // Parse requirements from product tags
      const parsed = BundleStateManager.parseProductRequirements(productData, variant);

      // Check if we need to update (only update if current values are defaults/undefined)
      // BUG-069 HARDENING: Use falsy checks instead of === false to handle undefined requirements
      const needsUpdate =
        (!product.requirements?.requiresPhoto && parsed.requiresPhoto) ||
        (!product.requirements?.requiresPetName && parsed.requiresPetName) ||
        ((product.requirements?.charLimit === undefined || product.requirements?.charLimit === 8) && parsed.charLimit !== 8) ||
        (!product.requirements?.noCaps && parsed.noCaps) ||
        ((product.pendantCount === undefined || product.pendantCount === 1) && parsed.pendantCount !== 1);

      if (needsUpdate) {
        hasChanges = true;
        return {
          ...product,
          pendantCount: parsed.pendantCount,
          requirements: {
            ...product.requirements,
            charLimit: parsed.charLimit,
            requiresPhoto: parsed.requiresPhoto,
            requiresPetName: parsed.requiresPetName,
            noCaps: parsed.noCaps
          }
        };
      }
      return product;
    });

    if (hasChanges) {
      this.stateManager.set({ products: updatedProducts });
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // STEP CHANGE HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle step change event
   * @param {string} newStep - The new step name
   * @param {Object} state - Current bundle state
   */
  onStepChange(newStep, state) {
    // Update visibility
    this.updateStepVisibility(newStep);
    this.updateStepIndicator(newStep);

    // Phase 9: Push step to browser history (unless navigating from popstate)
    if (!this.isNavigatingFromHistory) {
      this.pushStepToHistory(newStep);
    }
    this.isNavigatingFromHistory = false; // Reset flag

    // Initialize step-specific manager
    switch (newStep) {
      case 'selection':
        this.showSelectionStep();
        // BUG-097 FIX: Clear product cache when returning to selection
        // User may change products, so cache must be rebuilt on next step
        this.productsDataCache = null;
        // Also clear manager references since they hold stale cache
        if (this.individualManager) {
          this.individualManager.productsData = null;
        }
        if (this.togetherManager) {
          this.togetherManager.productsData = null;
        }
        if (this.summaryManager) {
          this.summaryManager.productsData = null;
        }
        break;
      case 'choice':
        this.initChoiceStep();
        break;
      case 'together':
        this.initTogetherStep();
        break;
      case 'individual-1':
      case 'individual-2':
      case 'individual-3':
        this.initIndividualStep(newStep);
        break;
      case 'upsells':
        this.initUpsellsStep();
        break;
      case 'summary':
        this.initSummaryStep();
        break;
    }
  }

  /**
   * Update visibility of step containers
   * Phase 9: Added exit animations before hiding
   * @param {string} currentStep - Current step name
   */
  updateStepVisibility(currentStep) {
    // Selection step: show product grid, hide others
    const productGrid = document.querySelector('.product-list');
    const togetherStep = document.getElementById('bundle-together-step');
    const individualStep = document.getElementById('bundle-individual-step');
    const upsellsStep = document.getElementById('bundle-upsells-step');
    const summaryStep = document.getElementById('bundle-summary-step');
    const sidebar = document.getElementById('bundle-builder-sidebar');
    // BUG-025: Selection step card-body contains "Next Step" button that must be hidden on other steps
    const selectionCardBody = document.querySelector('[data-bundle-step="selection"]');
    // BUG-086 FIX (v2): Use data attributes for title visibility
    const sidebarTitle = sidebar?.querySelector('[data-bundle-sidebar-title]');
    const mobileTitle = document.querySelector('[data-bundle-mobile-title]');
    // BUG-086 FIX (v3): Step 1 custom content (title + description)
    const step1Content = document.querySelector('[data-bundle-step1-content]');

    // BUG-125: Add step-specific body class for CSS targeting (mobile bar visibility)
    const stepClasses = ['bundle-step-selection', 'bundle-step-choice', 'bundle-step-together',
                         'bundle-step-individual', 'bundle-step-upsells', 'bundle-step-summary'];
    document.body.classList.remove(...stepClasses);
    const stepName = currentStep.startsWith('individual') ? 'individual' : currentStep;
    document.body.classList.add(`bundle-step-${stepName}`);

    // Phase 9: Helper to hide step with exit animation
    // EPIC-001 FIX: Now tracks timer IDs to allow cancellation
    const hideStepWithAnimation = (step) => {
      if (!step || step.hidden) return;

      // BUG-073 FIX: Skip hide animation for same-container transitions
      // When navigating individual-1 → individual-2, don't hide the individual container
      if (this.isSameContainerTransition_flag && step.id === 'bundle-individual-step') {
        return;
      }

      const stepId = step.id;

      // EPIC-001 FIX: Cancel any existing timer for this element before starting a new one
      if (stepId && this.hideTimers.has(stepId)) {
        clearTimeout(this.hideTimers.get(stepId));
        this.hideTimers.delete(stepId);
      }

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        // No animation - instant hide
        step.hidden = true;
      } else {
        // Add exit animation class
        step.classList.add('bundle-step-exiting');
        // After animation, hide - EPIC-001 FIX: Track the timer ID
        const timerId = setTimeout(() => {
          step.hidden = true;
          step.classList.remove('bundle-step-exiting');
          // Clean up timer reference
          if (stepId) this.hideTimers.delete(stepId);
        }, 200); // Match CSS animation duration
        // Store timer ID for potential cancellation
        if (stepId) this.hideTimers.set(stepId, timerId);
      }
    };

    // Phase 9: Helper to hide product grid with animation
    const hideGridWithAnimation = () => {
      if (!productGrid || productGrid.style.display === 'none') return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReducedMotion) {
        productGrid.style.display = 'none';
      } else {
        productGrid.classList.add('bundle-grid-exiting');
        setTimeout(() => {
          productGrid.style.display = 'none';
          productGrid.classList.remove('bundle-grid-exiting');
        }, 250);
      }
    };

    // Hide all step containers first
    hideStepWithAnimation(togetherStep);
    hideStepWithAnimation(individualStep);
    hideStepWithAnimation(upsellsStep);
    hideStepWithAnimation(summaryStep);

    // Show appropriate containers
    switch (currentStep) {
      case 'selection':
        // Header/footer hidden on ALL steps via bundle-page-active class (set in Liquid)
        // Removed: document.body.classList.remove('bundle-page-active') - was showing header on step 1
        if (productGrid) {
          productGrid.classList.remove('bundle-grid-exiting');
          productGrid.style.display = '';
        }
        if (sidebar) sidebar.querySelector('.card-body').style.display = '';
        // BUG-025: Show selection card-body (contains "Next Step" button)
        if (selectionCardBody) selectionCardBody.hidden = false;
        // BUG-086 FIX (v2): Both titles visible on step 1
        if (sidebarTitle) sidebarTitle.hidden = false;
        if (mobileTitle) mobileTitle.hidden = false;
        // BUG-086 FIX (v3): Step 1 content visible
        if (step1Content) step1Content.hidden = false;
        break;
      case 'choice':
        // BUG-100 FIX: Hide header on steps 2/3/4
        document.body.classList.add('bundle-page-active');
        // Choice is a modal, product grid stays visible
        if (productGrid) {
          productGrid.classList.remove('bundle-grid-exiting');
          productGrid.style.display = '';
        }
        // BUG-025: Selection card-body visible during choice (modal overlays it)
        if (selectionCardBody) selectionCardBody.hidden = false;
        // BUG-086 FIX (v2): Sidebar visible, mobile hidden (modal on mobile)
        if (sidebarTitle) sidebarTitle.hidden = false;
        if (mobileTitle) mobileTitle.hidden = true;
        // BUG-086 FIX (v3): Step 1 content hidden on steps 2/3/4
        if (step1Content) step1Content.hidden = true;
        break;
      case 'together':
        // BUG-100 FIX: Hide header on steps 2/3/4
        document.body.classList.add('bundle-page-active');
        // Hide product grid, show together step
        hideGridWithAnimation();
        if (togetherStep) {
          // EPIC-001 FIX: Cancel any pending hide timer before showing
          if (this.hideTimers.has('bundle-together-step')) {
            clearTimeout(this.hideTimers.get('bundle-together-step'));
            this.hideTimers.delete('bundle-together-step');
          }
          togetherStep.classList.remove('bundle-step-exiting');
          togetherStep.hidden = false;
        }
        // BUG-025: Hide selection card-body to prevent "Next Step" button clicks
        if (selectionCardBody) selectionCardBody.hidden = true;
        // BUG-086 FIX (v2): Sidebar visible (steps 1-3), mobile hidden (step 1 only)
        if (sidebarTitle) sidebarTitle.hidden = false;
        if (mobileTitle) mobileTitle.hidden = true;
        // BUG-086 FIX (v3): Step 1 content hidden on steps 2/3/4
        if (step1Content) step1Content.hidden = true;
        break;
      case 'individual-1':
      case 'individual-2':
      case 'individual-3':
        // BUG-100 FIX: Hide header on steps 2/3/4
        document.body.classList.add('bundle-page-active');
        // Hide product grid, show individual step
        hideGridWithAnimation();
        if (individualStep) {
          // EPIC-001 FIX: Cancel any pending hide timer before showing
          // This is the CRITICAL fix - when navigating individual-1 → individual-2,
          // the container is already visible so hideStepWithAnimation starts a 200ms timer.
          // Without cancellation, the timer fires AFTER we show the container, hiding it.
          if (this.hideTimers.has('bundle-individual-step')) {
            clearTimeout(this.hideTimers.get('bundle-individual-step'));
            this.hideTimers.delete('bundle-individual-step');
          }
          individualStep.classList.remove('bundle-step-exiting');

          // BUG-073 FIX: For same-container transitions, add class to disable enter animation
          // This prevents the CSS :not([hidden]) selector from triggering bundle-step-enter
          if (this.isSameContainerTransition_flag) {
            individualStep.classList.add('bundle-same-container-transition');
          }

          individualStep.hidden = false;
        }
        // BUG-025: Hide selection card-body to prevent "Next Step" button clicks
        if (selectionCardBody) selectionCardBody.hidden = true;
        // BUG-086 FIX (v2): Sidebar visible (steps 1-3), mobile hidden (step 1 only)
        if (sidebarTitle) sidebarTitle.hidden = false;
        if (mobileTitle) mobileTitle.hidden = true;
        // BUG-086 FIX (v3): Step 1 content hidden on steps 2/3/4
        if (step1Content) step1Content.hidden = true;
        break;
      case 'upsells':
        // BUG-100 FIX: Hide header on steps 2/3/4
        document.body.classList.add('bundle-page-active');
        // Hide product grid, show upsells step
        hideGridWithAnimation();
        if (upsellsStep) {
          // EPIC-001 FIX: Cancel any pending hide timer before showing
          if (this.hideTimers.has('bundle-upsells-step')) {
            clearTimeout(this.hideTimers.get('bundle-upsells-step'));
            this.hideTimers.delete('bundle-upsells-step');
          }
          upsellsStep.classList.remove('bundle-step-exiting');
          upsellsStep.hidden = false;
        }
        // BUG-025: Hide selection card-body to prevent "Next Step" button clicks
        if (selectionCardBody) selectionCardBody.hidden = true;
        // BUG-086 FIX (v2): Sidebar visible (steps 1-3), mobile hidden (step 1 only)
        if (sidebarTitle) sidebarTitle.hidden = false;
        if (mobileTitle) mobileTitle.hidden = true;
        // BUG-086 FIX (v3): Step 1 content hidden on steps 2/3/4
        if (step1Content) step1Content.hidden = true;
        break;
      case 'summary':
        // BUG-100 FIX: Hide header on steps 2/3/4
        document.body.classList.add('bundle-page-active');
        // Hide product grid, show summary step
        hideGridWithAnimation();
        if (summaryStep) {
          // EPIC-001 FIX: Cancel any pending hide timer before showing
          if (this.hideTimers.has('bundle-summary-step')) {
            clearTimeout(this.hideTimers.get('bundle-summary-step'));
            this.hideTimers.delete('bundle-summary-step');
          }
          summaryStep.classList.remove('bundle-step-exiting');
          summaryStep.hidden = false;
        }
        // BUG-025: Hide selection card-body to prevent "Next Step" button clicks
        if (selectionCardBody) selectionCardBody.hidden = true;
        // BUG-086 FIX (v2): Both titles hidden on step 4 (review shows all info)
        if (sidebarTitle) sidebarTitle.hidden = true;
        if (mobileTitle) mobileTitle.hidden = true;
        // BUG-086 FIX (v3): Step 1 content hidden on steps 2/3/4
        if (step1Content) step1Content.hidden = true;
        break;
    }
  }

  /**
   * Update step indicator progress bar
   * BUG-076 FIX: Replaced dot indicators with linear progress bar
   * @param {string} currentStep - Current step name
   */
  updateStepIndicator(currentStep) {
    const indicators = document.querySelectorAll('[data-bundle-step-indicator]');

    // Map steps to progress percentage and label index
    const stepConfig = {
      'selection': { percent: 0, labelIndex: 0 },
      'choice': { percent: 0, labelIndex: 0 },
      'together': { percent: 33, labelIndex: 1 },
      'individual-1': { percent: 33, labelIndex: 1 },
      'individual-2': { percent: 33, labelIndex: 1 },
      'individual-3': { percent: 33, labelIndex: 1 },
      'upsells': { percent: 66, labelIndex: 2 },
      'summary': { percent: 100, labelIndex: 3 }
    };

    const config = stepConfig[currentStep] || { percent: 0, labelIndex: 0 };

    indicators.forEach(indicator => {
      // Update progress bar fill
      const fill = indicator.querySelector('[data-bundle-progress-fill]');
      if (fill) {
        fill.style.width = `${config.percent}%`;
      }

      // Update labels (desktop sidebar + mobile bar - BUG-090)
      const labels = indicator.querySelectorAll('.bundle-progress-label');
      labels.forEach((label, index) => {
        label.classList.remove('active', 'completed');

        if (index < config.labelIndex) {
          label.classList.add('completed');
        } else if (index === config.labelIndex) {
          label.classList.add('active');
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // STEP INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Show selection step (return to product grid)
   */
  showSelectionStep() {
    // Hide any modals
    const choiceModal = document.getElementById('bundle-choice-modal');
    if (choiceModal) {
      const bsModal = bootstrap.Modal.getInstance(choiceModal);
      if (bsModal) bsModal.hide();
    }
  }

  /**
   * Initialize choice step (fetch products, show modal or auto-skip)
   */
  async initChoiceStep() {
    // Create choice manager if needed
    if (!this.choiceManager) {
      this.choiceManager = new BundleChoiceManager(
        this.stateManager,
        this.uiManager,
        this
      );
    }

    await this.choiceManager.init();
  }

  /**
   * Initialize together step
   * BUG-115 FIX: Made async to support re-fetching products when cache is missing
   * This was the ROOT CAUSE of Material selection not working - productsData was null
   */
  async initTogetherStep() {
    // Check if BundleTogetherManager is available (from custom-bundle-together.js)
    if (typeof BundleTogetherManager === 'undefined') {
      console.error('BundleTogetherManager not loaded');
      return;
    }

    // BUG-115 FIX: If cache is missing but we have products in state, RE-FETCH instead of redirecting
    // This happens when: Selection → Choice → Together → Back → Selection → forward to Together
    // The old code just redirected, but user has valid products - we should fetch their data
    if (!this.productsDataCache || this.productsDataCache.length < 3) {
      const state = this.stateManager.get();

      // If we have 3 products in state, re-fetch their data
      if (state.products && state.products.length === 3) {
        this.uiManager?.showLoading?.();

        try {
          const handles = state.products.map(p => p.productHandle);
          const responses = await Promise.allSettled(
            handles.map(handle =>
              fetch(`/products/${handle}.js`).then(r => {
                if (!r.ok) throw new Error(`HTTP ${r.status} for ${handle}`);
                return r.json();
              })
            )
          );

          const productsData = responses
            .filter(r => r.status === 'fulfilled')
            .map(r => r.value);

          if (productsData.length === 3) {
            this.productsDataCache = productsData;

            // Also recalculate common variants (they may have been cleared)
            const commonVariants = BundleStateManager.findCommonVariants(productsData);
            this.stateManager.set({
              hasCommonVariants: commonVariants.length > 0,
              commonVariants: commonVariants.length > 0 ? commonVariants : null
            });

            // Backfill requirements in case they were lost
            this.backfillProductRequirementsOnRestore(productsData);
          } else {
            throw new Error(`Could not fetch all products (got ${productsData.length}/3)`);
          }
        } catch (error) {
          console.error('[BundleRouter] Failed to re-fetch products:', error);
          this.uiManager?.showToast?.('Could not load products. Please start over.', 'error');
          this.stateManager.goToStep('selection');
          return;
        } finally {
          this.uiManager?.hideLoading?.();
        }
      } else {
        // No products in state - redirect to selection
        console.error('[BundleRouter] productsDataCache invalid and no products in state');
        this.uiManager?.showToast?.('Please select 3 products first.', 'warning');
        this.stateManager.goToStep('selection');
        return;
      }
    }

    // BUG-097 FIX: Validate cache matches current products
    if (!this.validateProductsCache()) {
      console.warn('[BundleRouter] Products cache mismatch - redirecting to choice step');
      this.uiManager?.showToast?.('Product selection changed. Please reconfigure.', 'info');
      this.stateManager.goToStep('choice');
      return;
    }

    // Create together manager if needed
    if (!this.togetherManager) {
      this.togetherManager = new BundleTogetherManager(
        this.stateManager,
        this.uiManager,
        this.productsDataCache
      );
    }

    // BUG-115 FIX: ALWAYS update productsData on the manager, not just in else branch
    // This ensures the manager always has the current data
    this.togetherManager.productsData = this.productsDataCache;

    this.togetherManager.init();
  }

  /**
   * Initialize individual step
   * @param {string} step - The individual step (individual-1, individual-2, individual-3)
   */
  initIndividualStep(step) {
    // Check if BundleIndividualManager is available (from custom-bundle-individual.js)
    if (typeof BundleIndividualManager === 'undefined') {
      console.error('BundleIndividualManager not loaded');
      return;
    }

    // Map step to product index
    const indexMap = { 'individual-1': 0, 'individual-2': 1, 'individual-3': 2 };
    const productIndex = indexMap[step];

    if (productIndex === undefined) return;

    // BUG-071 FIX: If productsDataCache is null, we can't proceed
    // This happens when page refreshes and initializeRestoredStep() failed,
    // or when navigating directly to individual step without going through choice
    if (!this.productsDataCache) {
      console.warn('[BundleRouter] productsDataCache is null - cannot init Individual step');
      this.uiManager?.showToast?.('Product data not loaded. Please start over.', 'warning');
      this.stateManager.goToStep('selection');
      return;
    }

    // BUG-097 FIX: Validate cache matches current products
    if (!this.validateProductsCache()) {
      console.warn('[BundleRouter] Products cache mismatch - redirecting to choice step');
      this.stateManager.goToStep('choice');
      return;
    }

    // Create individual manager if needed
    if (!this.individualManager) {
      this.individualManager = new BundleIndividualManager(
        this.stateManager,
        this.uiManager,
        this.productsDataCache
      );
    } else {
      // BUG-097 FIX: ALWAYS update manager's cache to current
      this.individualManager.productsData = this.productsDataCache;
    }

    this.individualManager.initForProduct(productIndex);
  }

  /**
   * Initialize upsells step
   */
  initUpsellsStep() {
    // Check if BundleUpsellsManager is available (from custom-bundle-upsells.js)
    if (typeof BundleUpsellsManager === 'undefined') {
      console.error('[BundleRouter] BundleUpsellsManager class not loaded');
      return;
    }

    // Create upsells manager if needed
    if (!this.upsellsManager) {
      this.upsellsManager = new BundleUpsellsManager(
        this.stateManager,
        this.uiManager
      );
    }

    this.upsellsManager.init();
  }

  /**
   * Initialize summary step
   */
  initSummaryStep() {
    // Check if BundleSummaryManager is available (from custom-bundle-upsells.js)
    if (typeof BundleSummaryManager === 'undefined') {
      console.error('BundleSummaryManager not loaded');
      return;
    }

    // BUG-097 FIX: Validate cache matches current products (consistency with other steps)
    if (!this.validateProductsCache()) {
      console.warn('[BundleRouter] Products cache mismatch - redirecting to choice step');
      this.stateManager.goToStep('choice');
      return;
    }

    // Create summary manager if needed
    if (!this.summaryManager) {
      this.summaryManager = new BundleSummaryManager(
        this.stateManager,
        this.uiManager,
        this.productsDataCache
      );
    } else {
      // Products may have changed since the manager was created
      this.summaryManager.productsData = this.productsDataCache;
    }

    this.summaryManager.init();
  }

  // ═══════════════════════════════════════════════════════════════
  // PHASE 9: BROWSER HISTORY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════

  /**
   * Setup browser history handling for back/forward navigation
   *
   * Why this solves the problem:
   * - Without this, browser back button navigates away from bundle builder entirely
   * - Users expect back to go to previous step, not leave the page
   * - We use history.pushState to add steps to browser history
   * - We handle popstate to intercept back/forward navigation
   */
  setupHistoryHandling() {
    // Initialize history state with current step
    const state = this.stateManager.get();
    const currentUrl = window.location.href;

    // Replace current state (don't push) to initialize
    history.replaceState(
      { bundleStep: state.currentStep },
      '',
      currentUrl
    );

    // Bind popstate handler
    this.boundPopstateHandler = (e) => this.handlePopstate(e);
    window.addEventListener('popstate', this.boundPopstateHandler);
  }

  /**
   * Handle browser back/forward button
   * @param {PopStateEvent} e - The popstate event
   */
  handlePopstate(e) {
    // If no bundle state in history, this is external navigation
    if (!e.state || !e.state.bundleStep) {
      return; // Let browser handle normally
    }

    const targetStep = e.state.bundleStep;
    const currentState = this.stateManager.get();

    // Set flag to prevent double history push
    this.isNavigatingFromHistory = true;

    // BUG-048 FIX: Use diagnostic method for specific error messages
    const diagnosis = this.stateManager.diagnoseStepAccess(targetStep);

    if (diagnosis.allowed) {
      // Navigate to the step from history
      this.stateManager.goToStep(targetStep);
    } else {
      // Navigate to recovery step with specific message
      this.stateManager.goToStep(diagnosis.recoveryStep);
      this.uiManager.showToast(diagnosis.reason, 'info');
    }
  }

  /**
   * Push current step to browser history
   * @param {string} step - Step name to push
   */
  pushStepToHistory(step) {
    // Don't push for selection (it's the initial state)
    // and don't push for choice (it's a modal)
    if (step === 'selection' || step === 'choice') {
      return;
    }

    // Push new state
    history.pushState(
      { bundleStep: step },
      '',
      window.location.href
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════

  /**
   * BUG-097 FIX: Validate that productsDataCache matches current state.products
   * Returns false if cache is stale or missing
   */
  validateProductsCache() {
    if (!this.productsDataCache || this.productsDataCache.length < 3) {
      return false;
    }

    const state = this.stateManager.get();
    if (!state.products || state.products.length < 3) {
      return false;
    }

    // Compare handles - order matters!
    const cacheHandles = this.productsDataCache.map(p => p.handle);
    const stateHandles = state.products.map(p => p.productHandle);

    for (let i = 0; i < 3; i++) {
      if (cacheHandles[i] !== stateHandles[i]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Cleanup event listeners
   */
  cleanup() {
    if (this.boundStepChangeHandler) {
      window.removeEventListener('bundle:step-changed', this.boundStepChangeHandler);
      this.boundStepChangeHandler = null;
    }

    // Phase 9: Remove popstate handler
    if (this.boundPopstateHandler) {
      window.removeEventListener('popstate', this.boundPopstateHandler);
      this.boundPopstateHandler = null;
    }

    if (this.choiceManager) {
      this.choiceManager.cleanup();
      this.choiceManager = null;
    }

    if (this.togetherManager) {
      this.togetherManager.cleanup();
      this.togetherManager = null;
    }

    if (this.individualManager) {
      this.individualManager.cleanup();
      this.individualManager = null;
    }

    if (this.upsellsManager) {
      this.upsellsManager.cleanup();
      this.upsellsManager = null;
    }

    if (this.summaryManager) {
      this.summaryManager.cleanup();
      this.summaryManager = null;
    }

    // EPIC-001 FIX: Clear all pending hide timers
    if (this.hideTimers) {
      this.hideTimers.forEach(timerId => clearTimeout(timerId));
      this.hideTimers.clear();
    }

    this.productsDataCache = null;
  }

  /**
   * Reinitialize for theme editor
   * BUG-055 FIX: Made async to await init()
   */
  async reinit() {
    this.cleanup();
    await this.init();
  }
}

// Export for other modules
window.BundleStepRouter = BundleStepRouter;

// ═══════════════════════════════════════════════════════════════════════════════
// BUNDLE CHOICE MANAGER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * BundleChoiceManager - Handles the choice step (Together vs Individual)
 *
 * Responsibilities:
 * - Fetches product JSON data for all 3 products
 * - Analyzes common variants using BundleStateManager.findCommonVariants()
 * - Shows choice modal OR auto-skips to individual if no common variants
 * - Handles mode selection
 */
class BundleChoiceManager {
  constructor(stateManager, uiManager, router) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.router = router;

    // Bound handlers for cleanup
    this.boundChoiceClickHandler = null;
    this.boundModalHiddenHandler = null;
    // BUG-064 FIX: Guard against rapid choice card clicks
    this.isChoiceInProgress = false;
    // BUG-063 FIX: Track if user made explicit choice in current modal session
    // Used to distinguish between "user cancelled" vs "user selected a mode"
    this.userMadeChoiceThisSession = false;
  }

  /**
   * Initialize the choice step
   * BUG-106 FIX: Complete architectural rewrite
   * - Fetch products FIRST (no modal yet - user sees selection screen)
   * - Check common variants BEFORE any modal decision
   * - If no common variants → go straight to individual (NO MODAL EVER)
   * - If has common variants → show modal with content ready (no loading spinner)
   */
  async init() {
    // Reset flags
    this.isChoiceInProgress = false;

    // Clean up any old modal handlers (prevents stale handler issues)
    const modal = document.getElementById('bundle-choice-modal');
    if (modal && this.boundModalHiddenHandler) {
      modal.removeEventListener('hidden.bs.modal', this.boundModalHiddenHandler);
      this.boundModalHiddenHandler = null;
    }

    try {
      // STEP 1: Fetch products FIRST - NO MODAL YET
      // User still sees selection screen during this brief fetch (1-2s)
      const productsData = await this.fetchProductsData();

      // Safety check: If user navigated away during fetch, abort
      const currentState = this.stateManager.get();
      if (currentState.currentStep !== 'choice') {
        this.uiManager.setNextButtonLoading(false);
        return;
      }

      // Validate we got all 3 products before caching or backfilling
      if (productsData.length < 3 || productsData.some(productData => !productData)) {
        throw new Error('Could not load all product data');
      }

      // Cache in router for later use by together manager
      this.router.productsDataCache = productsData;

      // Backfill requirements for products added via quick-add
      this.backfillProductRequirements(productsData);

      // STEP 2: Analyze common variants BEFORE any modal decision
      const commonVariants = BundleStateManager.findCommonVariants(productsData);
      this.stateManager.set({
        hasCommonVariants: commonVariants.length > 0,
        commonVariants: commonVariants.length > 0 ? commonVariants : null
      });

      // STEP 3: DECISION POINT
      if (commonVariants.length === 0) {
        // NO MODAL - go straight to individual mode
        // User's products have different options, no choice to make
        this.stateManager.set({
          customizationMode: 'individual',
          unifiedCustomization: null
        });
        // SESSION-1 FIX: Clear loading state before navigation
        this.uiManager.setNextButtonLoading(false);
        this.stateManager.goToStep('individual-1');
        return;
      }

      // STEP 4: User HAS a choice - show modal with content ready
      // Setup content BEFORE showing modal (no loading spinner)
      this.setupChoiceModalContent();
      // SESSION-1 FIX: Clear loading state before showing modal
      this.uiManager.setNextButtonLoading(false);
      this.showModalWithContent();

    } catch (error) {
      // Error: Show toast, return to selection (NO MODAL)
      // SESSION-1 FIX: Clear loading state on error
      this.uiManager.setNextButtonLoading(false);
      this.uiManager.showToast('Error loading products. Please try again.', 'error');
      this.stateManager.goToStep('selection');
    }
  }

  /**
   * Show modal immediately with loading state visible
   */
  showModalWithLoading() {
    const modal = document.getElementById('bundle-choice-modal');
    if (!modal) return;

    // Reset to loading state (in case returning to this step)
    const loading = modal.querySelector('[data-bundle-choice-loading]');
    const content = modal.querySelector('[data-bundle-choice-content]');

    if (loading) {
      loading.style.display = 'block';
      loading.classList.remove('fade-out');
    }
    if (content) {
      content.style.display = 'none';
      content.classList.remove('fade-in');
    }

    // Show the modal (will slide up with spinner visible)
    const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
    bsModal.show();
  }

  /**
   * Hide the choice modal
   */
  hideModal() {
    const modal = document.getElementById('bundle-choice-modal');
    if (modal) {
      // BUG-104 FIX: Use getOrCreateInstance to ensure modal always hides
      // getInstance can return null if instance was lost, causing modal to stay visible
      const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
      bsModal.hide();
    }
  }

  /**
   * BUG-106 FIX: Show modal with content visible (no loading state)
   * Only called when user actually has a choice to make
   * Content is set up BEFORE calling this method
   */
  showModalWithContent() {
    const modal = document.getElementById('bundle-choice-modal');
    if (!modal) return;

    const loading = modal.querySelector('[data-bundle-choice-loading]');
    const content = modal.querySelector('[data-bundle-choice-content]');

    // Hide loading, show content immediately (no spinner flash)
    if (loading) {
      loading.style.display = 'none';
    }
    if (content) {
      content.style.display = 'block';
      content.style.opacity = '1';
      content.classList.remove('fade-in');
    }

    // Show modal with content already visible
    const bsModal = bootstrap.Modal.getOrCreateInstance(modal);
    bsModal.show();
  }

  /**
   * Crossfade from loading state to content
   */
  transitionModalToContent() {
    const modal = document.getElementById('bundle-choice-modal');
    if (!modal) return;

    const loading = modal.querySelector('[data-bundle-choice-loading]');
    const content = modal.querySelector('[data-bundle-choice-content]');

    if (!loading || !content) return;

    // Fade out loading spinner
    loading.classList.add('fade-out');

    // After fade out completes, show content
    setTimeout(() => {
      loading.style.display = 'none';
      content.style.display = 'block';
      content.style.opacity = ''; // Clear any inline opacity to let CSS take over

      // Trigger reflow, then fade in content
      requestAnimationFrame(() => {
        content.classList.add('fade-in');
      });
    }, 250); // Match CSS transition duration
  }

  /**
   * Setup choice modal content (event handlers, card states)
   * Separated from showChoiceModal for new loading flow
   */
  setupChoiceModalContent() {
    const modal = document.getElementById('bundle-choice-modal');
    if (!modal) return;

    // BUG-040 FIX: Remove existing handlers before adding new ones
    if (this.boundChoiceClickHandler) {
      modal.removeEventListener('click', this.boundChoiceClickHandler);
    }
    if (this.boundModalHiddenHandler) {
      modal.removeEventListener('hidden.bs.modal', this.boundModalHiddenHandler);
    }

    // Configure Together card based on common variants
    const state = this.stateManager.get();
    const togetherCard = modal.querySelector('[data-bundle-choice="together"]');
    if (togetherCard) {
      if (!state.hasCommonVariants) {
        togetherCard.classList.add('disabled');
        togetherCard.style.opacity = '0.5';
        togetherCard.style.pointerEvents = 'none';
        let badge = togetherCard.querySelector('.bundle-choice-unavailable');
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'bundle-choice-unavailable text-muted small mt-2';
          badge.textContent = 'Not available - products have different options';
          togetherCard.appendChild(badge);
        }
      } else {
        togetherCard.classList.remove('disabled');
        togetherCard.style.opacity = '';
        togetherCard.style.pointerEvents = '';
        const badge = togetherCard.querySelector('.bundle-choice-unavailable');
        if (badge) badge.remove();
      }
    }

    // Bind click handlers for choice cards
    this.boundChoiceClickHandler = (e) => {
      if (this.isChoiceInProgress) return;

      const card = e.target.closest('[data-bundle-choice]');
      if (card && !card.classList.contains('disabled')) {
        const mode = card.dataset.bundleChoice;
        this.handleChoiceSelect(mode);
      }
    };
    modal.addEventListener('click', this.boundChoiceClickHandler);

    // Handle modal hidden event for navigation
    this.boundModalHiddenHandler = () => {
      if (!this.userMadeChoiceThisSession) {
        this.stateManager.goToStep('selection');
        return;
      }

      const state = this.stateManager.get();
      if (state.customizationMode === 'together') {
        this.stateManager.goToStep('together');
      } else if (state.customizationMode === 'individual') {
        this.stateManager.goToStep('individual-1');
      }

      this.isChoiceInProgress = false;
      this.userMadeChoiceThisSession = false;
    };
    modal.addEventListener('hidden.bs.modal', this.boundModalHiddenHandler);
  }

  /**
   * BUG-069 FIX: Update product requirements from fetched product data
   * This backfills requirements for products added via quick-add (which doesn't have tags)
   * @param {Array} productsData - Array of product JSON objects
   */
  backfillProductRequirements(productsData) {
    const state = this.stateManager.get();
    let hasChanges = false;

    const updatedProducts = state.products.map((product, index) => {
      const productData = productsData[index];
      if (!productData) return product;

      // Get the variant for pendant count extraction
      const variant = productData.variants?.find(v => v.id === product.selectedVariantId);

      // Parse requirements from product tags
      const parsed = BundleStateManager.parseProductRequirements(productData, variant);

      // Check if we need to update (only update if current values are defaults/undefined)
      // BUG-069 HARDENING: Use falsy checks instead of === false to handle undefined requirements
      const needsUpdate =
        (!product.requirements?.requiresPhoto && parsed.requiresPhoto) ||
        (!product.requirements?.requiresPetName && parsed.requiresPetName) ||
        ((product.requirements?.charLimit === undefined || product.requirements?.charLimit === 8) && parsed.charLimit !== 8) ||
        (!product.requirements?.noCaps && parsed.noCaps) ||
        ((product.pendantCount === undefined || product.pendantCount === 1) && parsed.pendantCount !== 1);

      if (needsUpdate) {
        hasChanges = true;
        return {
          ...product,
          pendantCount: parsed.pendantCount,
          requirements: {
            ...product.requirements,
            charLimit: parsed.charLimit,
            requiresPhoto: parsed.requiresPhoto,
            requiresPetName: parsed.requiresPetName,
            noCaps: parsed.noCaps
          }
        };
      }
      return product;
    });

    if (hasChanges) {
      this.stateManager.set({ products: updatedProducts });
    }
  }

  /**
   * Fetch product JSON for all 3 products in parallel
   * @returns {Promise<Array>} Product data objects by product index (null where a fetch failed)
   */
  async fetchProductsData() {
    const state = this.stateManager.get();
    const handles = state.products.map(p => p.productHandle);

    // BUG-104 FIX: Use fetchWithTimeout to prevent infinite loading on slow/dropped networks
    const responses = await Promise.allSettled(
      handles.map(handle =>
        BundleStateManager.fetchWithTimeout(
          `/products/${handle}.js`,
          {},
          10000  // 10 second timeout per product
        ).then(r => {
          if (!r.ok) throw new Error(`Failed to fetch ${handle}`);
          return r.json();
        })
      )
    );

    // Keep results aligned with state.products by index (failed fetches are null)
    return responses.map(r => (r.status === 'fulfilled' ? r.value : null));
  }

  /**
   * Handle mode selection from choice modal
   * @param {string} mode - 'together' or 'individual'
   */
  handleChoiceSelect(mode) {
    // BUG-083 FIX: Validate Together selection as safety net
    // If CSS disable is bypassed (inspect element, programmatic click), block here
    const state = this.stateManager.get();
    if (mode === 'together' && !state.hasCommonVariants) {
      return;
    }

    // BUG-063 FIX: Mark that user made an explicit choice
    // This distinguishes "user selected a mode" from "user cancelled"
    this.userMadeChoiceThisSession = true;

    // BUG-064 FIX: Lock to prevent rapid re-entry
    this.isChoiceInProgress = true;

    // BUG-065 FIX: Set mode but DO NOT navigate here.
    // Navigation happens in boundModalHiddenHandler after modal animation completes (300ms).
    // This prevents visual flicker/overlap where new step content appears while modal is still fading.
    this.stateManager.set({
      customizationMode: mode,
      unifiedCustomization: mode === 'together' ? {
        selectedOptions: {},
        photoUrl: null,
        petNames: []
      } : null
    });

    // Close modal - hidden handler will navigate after animation completes
    const modal = document.getElementById('bundle-choice-modal');
    if (modal) {
      const bsModal = bootstrap.Modal.getInstance(modal);
      if (bsModal) bsModal.hide();
    }
    // Navigation is handled by boundModalHiddenHandler (after modal fade animation)
  }

  /**
   * Cleanup event listeners
   */
  cleanup() {
    const modal = document.getElementById('bundle-choice-modal');
    if (modal) {
      if (this.boundChoiceClickHandler) {
        modal.removeEventListener('click', this.boundChoiceClickHandler);
      }
      if (this.boundModalHiddenHandler) {
        modal.removeEventListener('hidden.bs.modal', this.boundModalHiddenHandler);
      }
    }
    this.boundChoiceClickHandler = null;
    this.boundModalHiddenHandler = null;
    // BUG-064 FIX: Reset flag on cleanup
    this.isChoiceInProgress = false;
    // BUG-063 FIX: Reset choice tracking flag on cleanup
    this.userMadeChoiceThisSession = false;
  }
}

// Export for other modules
window.BundleChoiceManager = BundleChoiceManager;
